import { AABB3, MeasureEntity, MeasureSettings } from "@novorender/api";
import { ObjectId } from "@novorender/data-js-api";
import type { ReadonlyVec3, ReadonlyVec4 } from "gl-matrix";

export enum AsyncStatus {
    Initial,
    Loading,
    Success,
    Error,
}

export type AsyncEmpty = { status: Exclude<AsyncStatus, AsyncStatus.Success | AsyncStatus.Error> };
export type AsyncError = { status: AsyncStatus.Error; msg: string; stack?: string };
export type AsyncSuccess<T> = { status: AsyncStatus.Success; data: T };
export type AsyncState<T> = AsyncEmpty | AsyncError | AsyncSuccess<T>;

export function hasFinished<T>(state: AsyncState<T>): state is AsyncError | AsyncSuccess<T> {
    return state.status === AsyncStatus.Success || state.status === AsyncStatus.Error;
}

export function getAsyncStateData<T>(state: AsyncState<T>): T | undefined {
    if (state.status !== AsyncStatus.Success) {
        return;
    }

    return state.data;
}

export type ExtendedMeasureEntity = MeasureEntity & {
    settings?: MeasureSettings;
    connectionPoint?: ReadonlyVec3;
    isGenerated?: boolean;
};

export enum ViewMode {
    Default = "default",
    FollowPath = "followPath",
    Deviations = "deviations",
    CrossSection = "crossSection",
    Panorama = "panorama",
}

export type RecursivePartial<T> = { [P in keyof T]?: RecursivePartial<T[P]> };

// the NodeType in api does not work with --isolatedModules
export const NodeType = {
    Internal: 0,
    Leaf: 1,
};

// Redux toolkit with immer removes readonly modifier of state in the reducer so we get ts errors
// unless we cast the types to writable ones.
export type DeepMutable<T> = { -readonly [P in keyof T]: DeepMutable<T[P]> };

// Color expressed as a 4D vector with ranges [0.0 - 1.0] for red, green, blue and alpha components respectively.
export type ColorRGBA = ReadonlyVec4;

export type Color = {
    /** Hex color string: `#` followed by exactly six hexadecimal digits, e.g. `"#FF0000"`. */
    color: string;
    /** Opacity from 0 (fully transparent) to 1 (fully opaque), inclusive. */
    opacity: number;
};

/** Colors an object is drawn with, per render mode. Omitted modes keep their scene colors. */
export type Palette = {
    /** Color used in normal render mode. */
    default?: Color;
    /** Color used in X-Ray render mode. */
    xray?: Color;
    /** Color used while the object is selected. */
    selected?: Color;
};

/** Public identifier of an object in the model, as used by the `model` namespace APIs. */
export type PublicId = string;

export type CachedObject = {
    internalId: ObjectId;
    publicId: PublicId;
    /** Not loaded (e.g. resolve-only path). Use `PUBLIC_ID_NULL_VALUE` when the legacy id is known empty (search path). */
    oldPublicId?: PublicId;
    /**
     * - `undefined` — not loaded (including resolve: no bbox in response, so unknown if the object has bounds)
     * - `null` — loaded, known no bbox
     * - `AABB3` — loaded bounds
     */
    bbox?: AABB3 | null;
};

/** A palette together with the objects it applies to. */
export type PaletteParams = {
    /** Colors to draw the objects with. */
    palette: Palette;
    /** Public IDs of the objects to apply the palette to. */
    publicIds: PublicId[];
};

export type InternalPaletteParams = {
    palette: Palette;
    objectIds: ObjectId[];
};

export type SceneStatsRequest = {
    visibleFileCount?: boolean;
    excludedObjectCount?: boolean;
    /** Abort the read; the promise rejects with the signal's reason, an `AbortError` by default. */
    abortSignal?: AbortSignal;
};

export type SceneStatsResponse = {
    visibleFileCount?: number;
    excludedObjectCount?: number;
    /**
     * Whether {@link excludedObjectCount} accounts for active section planes. Absent when
     * that count was not asked for.
     */
    excludedObjectsRespectSectionPlanes?: boolean;
};

/** Optional flags for `setObjectColor`. */
export type SetObjectColorOptions = {
    /**
     * Also color descendants of the provided IDs, resolved via
     * `dataAccess.findIdsByProperties`.
     */
    includeDescendants?: boolean;
    /**
     * Clear all existing object colors after IDs are resolved and immediately
     * before applying the new palettes, so callers can replace colors without a
     * flicker between `clearAllObjectColor` and `setObjectColor`.
     */
    clear?: boolean;
};
